import React, {useState} from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { Grid, Slider, Typography, Box, Input, Alert  , Button, Link} from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { computePriceBasedOnQuantity, generateShippingCostText, isShippingIncludedFromXBottles, MAX_NUM_BOTTLES_PER_ORDER } from '@/utils/priceCalc';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { GOODFRIENDS } from '@/consts/siteConsts';
import { Cart } from '@/utils/cart';
import {priceInCentsToStrWithSymbol, applyDiscountOnPrice} from '@/utils/priceCalc';
import { Mixpanel } from '@/utils/mixpanel';
import Image from 'next/image'
import { useRouter } from "next/router";
import PartnersSupportClient from '@/utils/partnersSupportClient';
import { LoadingButton } from '@mui/lab';
import { getPackageNameForQuantity } from '@/src/utils/productUtils';
import { GOODFRIENDS_CA_LINK } from "@/src/utils/urlGenerator";

const PurchaseSelector = ({
  product,
  discountPercentage,
  cartUpdateControls = false,
}) => {
  const theme = useTheme()
  const router = useRouter()
  const [loading, setLoading] = useState(false);

  const purchaseInfo = product.purchaseInfo
  const seller = purchaseInfo?.seller
  const minBottles = Math.max(1, parseInt(purchaseInfo?.minBottles || 1))
  const maxBottles = parseInt(
    purchaseInfo?.max_amount_per_order || MAX_NUM_BOTTLES_PER_ORDER,
  )
  const defaultBottles = parseInt(
    purchaseInfo?.defaultBottlesToPurchase || minBottles,
  )

  const [amountInCart, setAmountInCart] = useState(() =>
    Cart.getProductAmount({ product }),
  )
  const [quantity, setQuantity] = useState(defaultBottles)
  
  let shipmentText,
    highlightShipmentMsg,
    shipmentIncluded,
    pricePerBottleInCents,
    noShipmentPriceDefined

  
  
  if (PartnersSupportClient.isShippingPriceBasedOnQuantity()) {
    const prices = computePriceBasedOnQuantity(product, quantity)
    pricePerBottleInCents = prices.pricePerBottleInCents
    const shipmentPriceInCents =
      prices.shipmentPriceInCents && parseInt(prices.shipmentPriceInCents)
    shipmentIncluded = shipmentPriceInCents === 1
    noShipmentPriceDefined = !shipmentPriceInCents
    highlightShipmentMsg = shipmentIncluded
    if (noShipmentPriceDefined) {
      shipmentText = 'Shipping is not possible for this number of bottles.'
    } else if (shipmentIncluded) {
      pricePerBottleInCents = pricePerBottleInCents + shipmentPriceInCents
      shipmentText = 'Shipping is included!'
    } else {
      shipmentText = `$${shipmentPriceInCents /
        100} Shipping will be added at checkout`
    }
  } else {
    pricePerBottleInCents = product.purchaseInfo?.price_cents
    highlightShipmentMsg = true
    shipmentText = seller && generateShippingCostText({product, seller})
  }

  if (!product.purchaseInfo) {
    return (
      <Box boxShadow={4} borderRadius={2} padding={4} maxWidth="400px">
        <Typography color="error" component="p" gutterBottom align="center">
          Prices are not defined for this product
        </Typography>
      </Box>
    )
  }
  const isPack = product.type === 'Gift Pack'

  const changeQuantity = ({ newValue, add, remove }) => {
    setQuantity((prev) => {
      let value = newValue
      if (newValue) {
        value = newValue
      } else if (add) {
        value = prev + 1
      } else if (remove) {
        value = prev - 1
      }
      value = Math.max(minBottles, value)
      value = Math.min(maxBottles, value)
      Mixpanel.track('Product - changeQuantity', { value })
      return value
    })
  }

  const handleSliderChange = (event, newValue) => {
    changeQuantity({ newValue })
  }

  const handleInputChange = (event) => {
    const newValue = event.target.value === '' ? '' : Number(event.target.value)
    changeQuantity({ newValue })
  }

  const handleBlur = () => {
    if (quantity < minBottles) {
      changeQuantity({ newValue: minBottles })
    } else if (quantity > maxBottles) {
      changeQuantity({ newValue: maxBottles })
    }
  }

  const addToCart = ({ navigateToCheckout }) => {
    Mixpanel.track('Add to cart')
    const { checkoutUrl } = Cart.updateProductAmount({ product, quantity })
    if (navigateToCheckout) {
      router.push(checkoutUrl)
    } else {
      setAmountInCart(quantity)
    }
  }

  
  const sliderLabel = getPackageNameForQuantity(product, quantity)
  let pricePer
  if (product.package_type?.singular) {
    pricePer = product.package_type.singular.toLowerCase()
  } else if (isPack) {
    pricePer = 'the entire pack'
  } else {
    pricePer = 'bottle'
  }

  let announcements = [purchaseInfo?.announcement, purchaseInfo?.announcement2]
  const from = !shipmentIncluded && isShippingIncludedFromXBottles(product)
  if (PartnersSupportClient.operatedByBestWine21()) {
    // no announcements
  } else if (from) {
    announcements.push(`Shipping included from ${from} ${pricePer}`)
    if (from - quantity === 1) {
      announcements.push(' (only 1 more bottle!)')
    } else if (from - quantity === 2) {
      announcements.push(' (only 2 more bottles!)')
    }
  }
  announcements = announcements.filter((v) => v)

  if (discountPercentage) {
    pricePerBottleInCents = applyDiscountOnPrice(
      pricePerBottleInCents,
      discountPercentage,
    )
  }

  let linkToOtherProducts = '/wines?ref=add_to_cart'

  const buyNowButton = (
    <LoadingButton
      loading={loading}
      disabled={noShipmentPriceDefined}
      onClick={() => {
        setLoading(true)
        addToCart({ navigateToCheckout: true })
      }}
      variant="contained"
      size="large"
      fullWidth
      sx={{ marginTop: 2, marginBottom: 1 }}
    >
      Buy Now
    </LoadingButton>
  )


  return (
    <Box
      boxShadow={4}
      borderRadius={2}
      padding={4}
      backgroundColor={theme.palette.background.paper}
      maxWidth="400px"
      margin="0 auto"
    >
      <Typography variant={'h4'} component="p" align="center">
        {priceInCentsToStrWithSymbol(pricePerBottleInCents)}
      </Typography>
      <Typography
        align="center"
        variant="caption"
        color="text.secondary"
        component="p"
        gutterBottom
      >
        {`Price is per ${pricePer}`}
      </Typography>

      {cartUpdateControls && (
        <>
          <Grid
            container
            spacing={2}
            alignItems="bottom"
            justifyContent="center"
          >
            <Grid item>
              <Input
                value={quantity}
                size="small"
                onChange={handleInputChange}
                onBlur={handleBlur}
                sx={{ fontSize: '1.25rem', fontWeight: 500 }}
                inputProps={{
                  step: 1,
                  min: minBottles,
                  max: maxBottles,
                  type: 'number',
                  'aria-labelledby': 'input-slider',
                }}
              />
            </Grid>
            <Grid item>
              <Typography variant="h6" id="input-slider" gutterBottom>
                {sliderLabel}
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Box
                display="flex"
                flexDirection={'column'}
                gap={1}
                alignItems="center"
                onClick={() => changeQuantity({ remove: true })}
              >
                <Image
                  height="64px"
                  width="20px"
                  style={{ cursor: 'pointer' }}
                  src={'/wine_bottle.png'}
                />
                <Typography variant="caption" color="text.secondary">
                  Remove
                </Typography>
              </Box>
            </Grid>
            <Grid item xs>
              <Slider
                step={1}
                marks
                min={minBottles}
                max={maxBottles}
                value={typeof quantity === 'number' ? quantity : 0}
                onChange={handleSliderChange}
                aria-labelledby="input-slider"
              />
            </Grid>
            <Grid item>
              <Box
                display="flex"
                flexDirection={'column'}
                gap={1}
                alignItems="center"
                onClick={() => changeQuantity({ add: true })}
              >
                <Image
                  height="64px"
                  width="50px"
                  style={{ cursor: 'pointer' }}
                  src={'/wine_bottles.png'}
                />
                <Typography variant="caption" color="text.secondary">
                  Add
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </>
      )}

      {cartUpdateControls ? (
        <Box
          margin={3}
          gap={1}
          display="flex"
          flexDirection={'column'}
          justifyContent="center"
        >
          <Button
            disabled={noShipmentPriceDefined}
            variant="outlined"
            size="large"
            onClick={() => addToCart({ navigateToCheckout: false })}
            fullWidth
          >
            {amountInCart ? 'Change Cart Quantity' : 'Add to Cart'}
          </Button>
          {amountInCart ? (
            <>
              <Typography
                variant="body2"
                align="center"
                color="success.main"
                sx={{ fontWeight: '600' }}
              >
                {`${amountInCart} ${getPackageNameForQuantity(
                  amountInCart,
                ).toLowerCase()} are in your cart`}
              </Typography>
              <Link href={linkToOtherProducts} variant={'body2'}>
                <Typography variant="body2" align="center">
                  Do you want to include other wines in your shipment?
                </Typography>
              </Link>
            </>
          ) : null}

          {buyNowButton}
        </Box>
      ) : (
        buyNowButton
      )}

      <Box display="flex" flexDirection={'column'} gap={1} marginTop={2}>
        {announcements.length > 0 && (
          <Box display="flex" flexDirection={'row'} gap={1} alignItems="center">
            <CampaignOutlinedIcon color={'secondary'} />
            <Box display="flex" flexDirection={'column'}>
              {announcements.map((announcement, key) => (
                <Typography
                  key={key}
                  color={theme.palette.secondary.main}
                  fontWeight={'700'}
                  variant="body2"
                >
                  {announcement}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
        {shipmentText ? (
          <Box display="flex" flexDirection={'row'} gap={1} alignItems="center">
            <LocalShippingOutlinedIcon
              color={highlightShipmentMsg ? 'secondary' : undefined}
            />
            <Typography
              variant="body2"
              fontWeight={highlightShipmentMsg ? '700' : undefined}
              color={
                highlightShipmentMsg ? theme.palette.secondary.main : undefined
              }
              sx={{ whiteSpace: 'pre-line' }}
            >
              {shipmentText}
            </Typography>


          </Box>
        ) : null}

        {seller?.key === GOODFRIENDS ? (
          <Alert severity="warning" variant='outlined' icon={false} sx={{ marginTop: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Typography sx={{ fontWeight: 'bold' }}>
              Shipping to California?
              <Link href={GOODFRIENDS_CA_LINK}> click here</Link>
            </Typography>
          </Alert>
        ) : null }
        
      </Box>
    </Box>
  )
}

PurchaseSelector.propTypes = {
  product: PropTypes.object.isRequired,
  discountPercentage: PropTypes.number,
};


export default PurchaseSelector;
